import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

/**
 * Solicita permiso para recibir notificaciones del navegador
 * @param userId ID del usuario actual
 * @returns Promise<boolean> True si se concedió el permiso, false en caso contrario
 */
export const requestNotificationPermission = async (userId: string): Promise<boolean> => {
  try {
    // En Android WebView, las notificaciones se manejan nativamente
    if ((window as any).Android && (window as any).Android.showNativeNotification) {
      console.log('Usando notificaciones nativas de Android');
      return true;
    }

    // Verificar si el navegador soporta notificaciones
    if (!('Notification' in window)) {
      console.log('Este navegador no soporta notificaciones de escritorio');
      return false;
    }

    // Solicitar permiso
    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      try {
        // Verificar compatibilidad con Firebase Messaging antes de inicializar
        const supported = await isSupported();
        if (!supported) {
          console.log('Firebase Messaging no es compatible en este navegador/entorno.');
          // Aunque FCM no esté disponible, el permiso del navegador fue concedido
          return true;
        }
        // Registrar el token de FCM
        const messaging = getMessaging();
        const token = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
        });

        if (token) {
          // Guardar el token en Firestore
          await saveUserFCMToken(userId, token);

          // Configurar el manejo de mensajes en primer plano
          setupForegroundNotifications();

          return true;
        } else {
          console.log('No se pudo obtener el token de FCM');
          return false;
        }
      } catch (error) {
        console.error('Error al registrar token de FCM:', error);
        // Aunque falle FCM, el permiso fue concedido
        return true;
      }
    } else {
      console.log('Permiso de notificación denegado');
      return false;
    }
  } catch (error) {
    console.error('Error al solicitar permiso de notificación:', error);
    return false;
  }
};

/**
 * Guarda el token FCM del usuario en Firestore
 * @param userId ID del usuario
 * @param token Token FCM
 * @returns Promise<void>
 */
export const saveUserFCMToken = async (userId: string, token: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      // Verificar si el token ya está guardado para evitar duplicados
      const userData = userDoc.data();
      const tokens = userData.fcmTokens || [];

      if (!tokens.includes(token)) {
        // Añadir el nuevo token a la lista
        await updateDoc(userRef, {
          fcmTokens: [...tokens, token]
        });
        console.log('Token FCM guardado correctamente');
      }
    }
  } catch (error) {
    console.error('Error al guardar token FCM:', error);
  }
};

/**
 * Configura el manejo de notificaciones cuando la app está en primer plano
 */
export const setupForegroundNotifications = (): void => {
  try {
    // Ejecutar de forma asíncrona para poder verificar compatibilidad primero
    (async () => {
      try {
        const supported = await isSupported();
        if (!supported) {
          console.log('Firebase Messaging no es compatible en este navegador/entorno.');
          return;
        }
        const messaging = getMessaging();
        onMessage(messaging, (payload) => {
          console.log('Mensaje recibido en primer plano:', payload);

          // Mostrar notificación tipo "toast" en la interfaz
          if (payload.notification) {
            const { title, body } = payload.notification;

            // Crear y mostrar notificación nativa si está en segundo plano
            if (document.visibilityState === 'hidden') {
              const notification = new Notification(title || 'DomiRed', {
                body: body || 'Tienes una nueva notificación',
                icon: '/logo192.png'
              });

              // Manejar clic en la notificación
              notification.onclick = () => {
                window.focus();
                notification.close();

                // Navegar a la página correspondiente si hay datos
                if (payload.data && payload.data.url) {
                  window.location.href = payload.data.url;
                }
              };
            } else {
              // Si la app está visible, mostrar un toast (implementado en otro componente)
              // Disparar un evento personalizado que será capturado por el componente Toast
              const event = new CustomEvent('showToast', {
                detail: {
                  title: title || 'DomiRed',
                  message: body || 'Tienes una nueva notificación',
                  type: 'info',
                  url: payload.data?.url
                }
              });
              window.dispatchEvent(event);
            }
          }
        });
      } catch (innerError) {
        console.error('Error interno al configurar notificaciones en primer plano:', innerError);
      }
    })();
  } catch (error) {
    console.error('Error al configurar notificaciones en primer plano:', error);
  }
};

/**
 * Envía una notificación del navegador
 * En Android WebView, usa el puente nativo para mostrar notificaciones del sistema
 * @param title Título de la notificación
 * @param body Cuerpo de la notificación
 * @param url URL a la que dirigir al hacer clic (opcional)
 */
export const showBrowserNotification = (title: string, body: string, url?: string): void => {
  try {
    // Si estamos en el WebView de Android, usar notificaciones nativas
    if ((window as any).Android && (window as any).Android.showNativeNotification) {
      (window as any).Android.showNativeNotification(title, body, url || '');
      return;
    }

    // Fallback: notificación del navegador (para desktop)
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }

    const notification = new Notification(title, {
      body,
      icon: '/logo192.png'
    });

    notification.onclick = () => {
      window.focus();
      notification.close();

      if (url) {
        window.location.href = url;
      }
    };
  } catch (error) {
    console.error('Error al mostrar notificación:', error);
  }
};
